import { useEffect, useRef } from 'react';
import api from '../utils/api';

/**
 * Pings POST /users/me/heartbeat every 60s while the tab is visible.
 *
 * The Team Hours page uses the timestamp of the last heartbeat to flip a
 * punched-in user to "Away" after 10 minutes of silence. Because *every*
 * browser tab goes `visibilityState=hidden` when the OS locks the screen,
 * skipping heartbeats while hidden is a clean cross-browser proxy for
 * "system locked" without needing the experimental Idle Detection API.
 */
export function useHeartbeat() {
  const timerRef = useRef(null);

  useEffect(() => {
    const send = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        api.post('/users/me/heartbeat').catch(() => { /* silent — interceptor handles 401 */ });
      }
    };

    // First ping right away so a freshly-logged-in user is "working" instantly.
    send();
    timerRef.current = setInterval(send, 60_000);

    // When the tab becomes visible again (unlock / refocus), ping immediately
    // so the user doesn't stay "Away" for up to a full minute.
    const onVisibility = () => { if (document.visibilityState === 'visible') send(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}
